'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Plus, Search, SlidersHorizontal, FolderOpen, Calendar, CheckSquare } from 'lucide-react';
import { PROJECTS, type Project, type ProjectStatus } from '@/lib/mockData';
import ProgressBar from '@/components/ui/ProgressBar';
import StatusBadge from '@/components/ui/StatusBadge';
import AvatarGroup from '@/components/ui/AvatarGroup';
import CreateProjectModal from './CreateProjectModal';
import EmptyState from '@/components/ui/EmptyState';

type FilterStatus = 'All' | ProjectStatus;

export default function ProjectsGrid() {
  const [projects, setProjects] = useState<Project[]>(PROJECTS);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('All');
  const [showModal, setShowModal] = useState(false);

  const filtered = projects.filter((p) => {
    const matchSearch =
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.description.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'All' || p.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const handleCreateProject = (data: Omit<Project, 'id' | 'tasks' | 'progress'>) => {
    // Backend integration point: POST /api/projects
    const newProject: Project = {
      ...data,
      id: `proj-${Date.now()}`,
      tasks: [],
      progress: 0,
    };
    setProjects((prev) => [newProject, ...prev]);
    setShowModal(false);
  };

  const filterOptions: FilterStatus[] = ['All', 'Active', 'On Hold', 'Completed'];

  return (
    <div className="p-6 lg:p-8 max-w-screen-2xl mx-auto fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Projects</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {projects.length} projects · {projects.filter((p) => p.status === 'Active').length} active
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium self-start sm:self-auto"
        >
          <Plus size={16} />
          New Project
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-xs">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-border rounded-lg outline-none focus:ring-2 focus:ring-primary/30 transition-all"
          />
        </div>
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={15} className="text-muted-foreground" />
          {filterOptions.map((opt) => (
            <button
              key={`filter-${opt}`}
              onClick={() => setFilterStatus(opt)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 ${
                filterStatus === opt
                  ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-border'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <EmptyState
          icon={<FolderOpen size={28} className="text-muted-foreground" />}
          title="No projects found"
          description="No projects match your current filters. Try adjusting your search or create a new project."
          action={{ label: 'Create New Project', onClick: () => setShowModal(true) }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
          {filtered.map((project) => {
            const doneTasks = project.tasks.filter((t) => t.status === 'Done').length;
            const inProgressTasks = project.tasks.filter((t) => t.status === 'In Progress').length;

            return (
              <Link
                key={`proj-card-${project.id}`}
                href="/project-detail"
                className="bg-card rounded-xl border border-border p-5 hover:shadow-card-hover hover:border-primary/30 transition-all duration-200 group block"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FolderOpen size={20} className="text-primary" />
                  </div>
                  <StatusBadge status={project.status} />
                </div>

                {/* Title & description */}
                <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors mb-1">
                  {project.name}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                  {project.description}
                </p>

                {/* Stats row */}
                <div className="flex items-center gap-4 mb-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <CheckSquare size={13} />
                    <span>{doneTasks}/{project.tasks.length} done</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    <span>{inProgressTasks} in progress</span>
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-4 space-y-1">
                  <ProgressBar
                    value={project.progress}
                    showLabel
                    size="md"
                    color={
                      project.status === 'On Hold' ?'amber'
                        : project.progress >= 80
                        ? 'green' :'primary'
                    }
                  />
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <AvatarGroup memberIds={project.teamMemberIds} max={3} size="sm" />
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Calendar size={12} />
                    <span>Due {project.endDate}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {showModal && (
        <CreateProjectModal
          onClose={() => setShowModal(false)}
          onCreate={handleCreateProject}
        />
      )}
    </div>
  );
}